/**
 * Размер и поворот стикера — как в мессенджерах.
 *
 * Выделили стикер — вокруг рамка: за углы его растягивают, за кружок
 * сверху крутят. Размер и поворот — в пикселях экрана, а не в ценах и
 * времени: стикер не должен сплющиваться, когда график масштабируют.
 *
 * Опоры по номерам: 0–3 — углы (левый верхний, правый верхний, правый
 * нижний, левый нижний), 4 — вращалка над верхней стороной.
 */

import type { Drawing } from './types.js';

export interface StickerPose {
  /** Сторона стикера, пикселей. */
  readonly size: number;
  /** Поворот, радианы; 0 — ровно. */
  readonly rotation: number;
}

export const MIN_STICKER = 56;
export const MAX_STICKER = 360;
/** Номер опоры-вращалки. */
export const ROTATE_HANDLE = 4;
/** Насколько вращалка выше верхней стороны, пикселей. */
const ROTATE_REACH = 26;

interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Размер и поворот стикера.
 *
 * Стикеры, поставленные до рамки, размера не знали: он брался из
 * толщины (S, M, L, XL). Для них — та же величина, что была видна.
 */
export function poseOf(drawing: Pick<Drawing, 'width' | 'pose'>): StickerPose {
  if (drawing.pose) return drawing.pose;
  return { size: 68 + Math.max(1, drawing.width) * 24, rotation: 0 };
}

/** Повернуть смещение (dx, dy) на угол. */
function turn(dx: number, dy: number, angle: number): Point {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: dx * cos - dy * sin, y: dx * sin + dy * cos };
}

/** Опоры стикера на экране: четыре угла и вращалка. */
export function stickerHandles(centre: Point, pose: StickerPose): Point[] {
  const half = pose.size / 2;
  const at = (dx: number, dy: number): Point => {
    const p = turn(dx, dy, pose.rotation);
    return { x: centre.x + p.x, y: centre.y + p.y };
  };
  return [at(-half, -half), at(half, -half), at(half, half), at(-half, half), at(0, -half - ROTATE_REACH)];
}

/** Середина верхней стороны — от неё к вращалке идёт тонкая линия. */
export function topMiddle(centre: Point, pose: StickerPose): Point {
  const p = turn(0, -pose.size / 2, pose.rotation);
  return { x: centre.x + p.x, y: centre.y + p.y };
}

/** Место, занятое повёрнутым стикером, — для попадания курсором. */
export function stickerBounds(centre: Point, pose: StickerPose): { left: number; top: number; right: number; bottom: number } {
  const corners = stickerHandles(centre, pose).slice(0, 4);
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  return { left: Math.min(...xs), top: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys) };
}

/** Шаг поворота с Shift и «прилипание» к ровному положению. */
const SNAP_STEP = Math.PI / 12;
const SNAP_LEVEL = (4 * Math.PI) / 180;

/**
 * Новый размер или поворот, когда тянут опору.
 *
 * Угол тянет размер равномерно — стикер не сплющивается. Вращалка
 * поворачивает вокруг центра; рядом с ровным положением стикер к нему
 * прилипает, с Shift поворачивается шагами по 15°.
 */
export function poseDragged(pose: StickerPose, handle: number, centre: Point, cursor: Point, snap = false): StickerPose {
  const dx = cursor.x - centre.x;
  const dy = cursor.y - centre.y;
  if (handle === ROTATE_HANDLE) {
    // Вращалка стоит над центром: её угол — поворот плюс четверть оборота.
    let rotation = Math.atan2(dy, dx) + Math.PI / 2;
    rotation = Math.atan2(Math.sin(rotation), Math.cos(rotation));
    if (snap) rotation = Math.round(rotation / SNAP_STEP) * SNAP_STEP;
    else if (Math.abs(rotation) < SNAP_LEVEL) rotation = 0;
    return { size: pose.size, rotation };
  }
  // Угол квадрата отстоит от центра на половину диагонали.
  const size = Math.hypot(dx, dy) * Math.SQRT2;
  return { size: Math.round(Math.min(MAX_STICKER, Math.max(MIN_STICKER, size))), rotation: pose.rotation };
}

/** Приведение сохранённого размера и поворота. */
export function normalizePose(value: unknown): StickerPose | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Record<string, unknown>;
  if (typeof raw.size !== 'number' || !Number.isFinite(raw.size)) return undefined;
  const rotation = typeof raw.rotation === 'number' && Number.isFinite(raw.rotation) ? raw.rotation : 0;
  return { size: Math.min(MAX_STICKER, Math.max(MIN_STICKER, raw.size)), rotation };
}
